import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DocumentLoadError } from '@/attachment/component/DocumentLoadError';
import { DocumentViewer } from '@/attachment/component/DocumentViewer';
import { Button } from '@/common/components/ui/button';
import { Skeleton } from '@/common/components/ui/skeleton';

import { useFetchBillingDocument } from '../hooks/useFetchBillingDocument';

interface BillingDocumentPanelProps {
  billingUuid: string;
  documentId: number;
  onClose: () => void;
}

export const BillingDocumentPanel = ({
  billingUuid,
  documentId,
  onClose,
}: BillingDocumentPanelProps) => {
  const { t } = useTranslation();
  const { documentBlob, isLoading, isError } = useFetchBillingDocument(
    billingUuid,
    documentId,
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Skeleton className="h-full rounded-none bg-muted-foreground/20" />
      );
    }

    if (isError || !documentBlob) {
      return <DocumentLoadError />;
    }

    return <DocumentViewer blob={documentBlob} documentId={documentId} />;
  };

  return (
    <section
      aria-label={t('billing.drawer.document.viewer')}
      className="fixed z-[60] lg:z-40 left-0 right-0 lg:right-[32rem] top-13.75 h-[calc(100vh-55px)] flex flex-col border-r bg-muted"
    >
      <div className="flex justify-end p-2 lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={t('billing.drawer.document.close')}
        >
          <X />
        </Button>
      </div>
      <div className="flex-1 min-h-0">{renderContent()}</div>
    </section>
  );
};
